import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, logout, User, isAuthenticated, login as authLogin } from '../services/auth';

// Определяем тип контекста авторизации
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

// Создаем контекст с начальным значением
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Хук для использования контекста авторизации
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Компонент-провайдер для контекста авторизации
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Состояние для хранения данных пользователя
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const refreshUser = async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
      setError(null);
    } catch (err) {
      setUser(null);
      setError('Не удалось получить данные пользователя');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const success = await authLogin(username, password);
      if (success) {
        await refreshUser();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Ошибка при входе:', err);
      return false;
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    try {
      const isAuth = await isAuthenticated();
      if (!isAuth) {
        setUser(null);
      }
      return isAuth;
    } catch (err) {
      setUser(null);
      console.error('Ошибка при проверке авторизации:', err);
      return false;
    }
  };

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  useEffect(() => {
    refreshUser();
  }, []);

  // Значение контекста
  const value = {
    user,
    loading,
    error,
    login,
    logout: handleLogout,
    refreshUser,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 